import React, { useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import ReactECharts from 'echarts-for-react';
import { HeatmapCell } from '../hooks/useOverviewData';
import { formatNumber } from '../utils/format';
import { AV2_CHART } from '../utils/chartTheme';

interface Props {
    cells: HeatmapCell[] | null;
    loading: boolean;
}

const HOURS_PER_DAY = 24;
const DAYS = 7;

// 0=Mon … 6=Sun, matches backend MOD(DAYOFWEEK-2+7,7)
const DAY_KEYS = [
    'analyticsV2.heatmap.dow.mon',
    'analyticsV2.heatmap.dow.tue',
    'analyticsV2.heatmap.dow.wed',
    'analyticsV2.heatmap.dow.thu',
    'analyticsV2.heatmap.dow.fri',
    'analyticsV2.heatmap.dow.sat',
    'analyticsV2.heatmap.dow.sun',
];

/**
 * 7 × 24 completion-count heatmap (ECharts). x = hour of day, y = weekday,
 * color = completions in that slot (visualMap scale). Native hover/tap tooltip
 * shows the exact day, hour, and count. (Backend densifies a sparse payload of
 * { dow, hour, count } cells.)
 */
const ActivityHeatmapCard: React.FC<Props> = ({ cells, loading }) => {
    const intl = useIntl();
    const locale = intl.locale;

    const matrix = useMemo(() => {
        const grid: number[][] = Array.from({ length: DAYS }, () =>
            Array.from({ length: HOURS_PER_DAY }, () => 0),
        );
        (cells ?? []).forEach((c) => {
            if (c.dow >= 0 && c.dow < DAYS && c.hour >= 0 && c.hour < HOURS_PER_DAY) {
                grid[c.dow][c.hour] = c.count;
            }
        });
        return grid;
    }, [cells]);

    const maxCount = useMemo(() => {
        let m = 0;
        for (let d = 0; d < DAYS; d++) {
            for (let h = 0; h < HOURS_PER_DAY; h++) {
                if (matrix[d][h] > m) m = matrix[d][h];
            }
        }
        return m;
    }, [matrix]);

    const totalCount = useMemo(() => {
        let total = 0;
        (cells ?? []).forEach((c) => {
            total += c.count;
        });
        return total;
    }, [cells]);

    const isEmpty = cells != null && totalCount === 0;

    const option = useMemo((): any => {
        const dayLabels = DAY_KEYS.map((k) => intl.formatMessage({ id: k, defaultMessage: '' }));
        const hourLabels = Array.from({ length: HOURS_PER_DAY }, (_, h) => String(h).padStart(2, '0'));
        const points: [number, number, number][] = [];
        for (let d = 0; d < DAYS; d++) {
            for (let h = 0; h < HOURS_PER_DAY; h++) {
                points.push([h, d, matrix[d][h]]);
            }
        }

        const lessLabel = intl.formatMessage({ id: 'analyticsV2.heatmap.legend.less', defaultMessage: 'less' });
        const moreLabel = intl.formatMessage({ id: 'analyticsV2.heatmap.legend.more', defaultMessage: 'more' });

        return {
            textStyle: { fontFamily: AV2_CHART.font },
            tooltip: {
                position: 'top',
                backgroundColor: AV2_CHART.tooltipBg,
                borderWidth: 0,
                padding: [8, 12],
                textStyle: { color: '#ffffff', fontSize: 12, fontFamily: AV2_CHART.font },
                formatter: (p: any) => {
                    const [h, d, c] = p.data as [number, number, number];
                    const when = `${dayLabels[d]} · ${hourLabels[h]}:00`;
                    const line = intl.formatMessage(
                        {
                            id: 'analyticsV2.heatmap.tooltip',
                            defaultMessage: '{count, plural, one {# completion} other {# completions}}',
                        },
                        { count: c },
                    );
                    return `<div style="font-weight:600;margin-bottom:2px">${when}</div>${line}`;
                },
            },
            grid: { left: 4, right: 8, top: 14, bottom: 56, containLabel: true },
            xAxis: {
                type: 'category',
                data: hourLabels,
                splitArea: { show: false },
                axisTick: { show: false },
                axisLine: { show: false },
                axisLabel: { color: AV2_CHART.textTertiary, fontSize: 10, interval: 2, fontFamily: AV2_CHART.font },
            },
            yAxis: {
                type: 'category',
                data: dayLabels,
                inverse: true,
                splitArea: { show: false },
                axisTick: { show: false },
                axisLine: { show: false },
                axisLabel: { color: AV2_CHART.textSecondary, fontSize: 11, fontFamily: AV2_CHART.font },
            },
            visualMap: {
                type: 'continuous',
                min: 0,
                max: Math.max(maxCount, 1),
                calculable: false,
                orient: 'horizontal',
                left: 'center',
                bottom: 8,
                itemWidth: 14,
                itemHeight: 140,
                text: [moreLabel, lessLabel],
                textStyle: { color: AV2_CHART.textTertiary, fontSize: 11, fontFamily: AV2_CHART.font },
                inRange: { color: ['#eef2ff', '#c7d2fe', '#6366f1'] },
            },
            series: [
                {
                    type: 'heatmap',
                    data: points,
                    itemStyle: { borderColor: '#ffffff', borderWidth: 2, borderRadius: 3 },
                    emphasis: { itemStyle: { borderColor: AV2_CHART.textSecondary, borderWidth: 1.5 } },
                },
            ],
        };
    }, [matrix, maxCount, intl]);

    return (
        <section className="av2-card av2-heatmap">
            <div className="av2-card__header">
                <div className="av2-card__title-wrap">
                    <h3 className="av2-card__title">
                        <FormattedMessage id="analyticsV2.heatmap.title" defaultMessage="Activity rhythm" />
                    </h3>
                    <p className="av2-card__subtitle">
                        <FormattedMessage
                            id="analyticsV2.heatmap.subtitle"
                            defaultMessage="When work actually gets done — completions per weekday and hour"
                        />
                    </p>
                </div>
                {totalCount > 0 && (
                    <div className="av2-heatmap__total">
                        <span className="av2-heatmap__total-value">{formatNumber(totalCount, locale)}</span>
                        <span className="av2-heatmap__total-label">
                            <FormattedMessage id="analyticsV2.heatmap.totalCompletions" defaultMessage="completions" />
                        </span>
                    </div>
                )}
            </div>
            <div className="av2-card__body">
                {loading || !cells ? (
                    <div className="av2-skel av2-skel--chart" />
                ) : isEmpty ? (
                    <div className="av2-empty">
                        <FormattedMessage
                            id="analyticsV2.heatmap.empty"
                            defaultMessage="No completions recorded in this period."
                        />
                    </div>
                ) : (
                    <div
                        className="av2-heatmap__chart"
                        role="img"
                        aria-label={intl.formatMessage({
                            id: 'analyticsV2.heatmap.ariaLabel',
                            defaultMessage: 'Heatmap of completions across days and hours',
                        })}
                    >
                        <ReactECharts option={option} notMerge style={{ height: 300, width: '100%' }} opts={{ renderer: 'svg' }} />
                    </div>
                )}
            </div>
        </section>
    );
};

export default ActivityHeatmapCard;
